import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../utils/api';

const SOURCES = [
  { value: 'google',   label: '🔵 Google', color: '#4285F4' },
  { value: 'facebook', label: '🔷 Facebook', color: '#1877F2' },
  { value: 'justdial', label: '🟠 JustDial', color: '#FF6600' },
  { value: 'direct',   label: '⭐ Direct', color: '#F5A800' },
];

function StarRating({ value, onChange, readOnly = false, size = '1.3rem' }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: 'flex', gap: '0.2rem' }}>
      {[1, 2, 3, 4, 5].map(star => (
        <span key={star}
          onClick={() => !readOnly && onChange && onChange(star)}
          onMouseEnter={() => !readOnly && setHovered(star)}
          onMouseLeave={() => !readOnly && setHovered(0)}
          style={{
            fontSize: size, cursor: readOnly ? 'default' : 'pointer',
            color: star <= (hovered || value) ? '#f59e0b' : 'var(--ink-5)',
            transition: 'color 0.2s',
          }}>★</span>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [data, setData]         = useState({ reviews: [], avgRating: 0, total: 0 });
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating]     = useState(5);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    api.get('/reviews').then(r => setData({ reviews: Array.isArray(r.data)?r.data:(r.data?.reviews||[]), avgRating: r.data?.avgRating||5, total: r.data?.total||(Array.isArray(r.data)?r.data.length:0) })).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const onSubmit = async (formData) => {
    if (rating === 0) { toast.error('Please select a star rating'); return undefined; }
    setSubmitting(true);
    try {
      await api.post('/reviews/submit', { ...formData, rating });
      toast.success('Thank you! Your review will appear after verification.');
      reset(); setRating(5); setShowForm(false);
    } catch (err) {
      toast.error('Failed to submit. Please try again.');
    } finally { setSubmitting(false); }
  };

  const ratingBreakdown = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: data.reviews.filter(r => r.rating === star).length,
    pct: data.reviews.length ? Math.round((data.reviews.filter(r => r.rating === star).length / data.reviews.length) * 100) : 0,
  }));

  return (
    <>
      <Helmet>
        <title>Client Reviews & Testimonials | Wedoo Interiors Tumakuru</title>
        <meta name="description" content="Read verified client reviews for Wedoo Interiors — interior design, modular kitchens and home transformation projects in Tumakuru, Karnataka." />
        <meta name="keywords" content="wedoo interiors reviews, interior designer reviews tumakuru, modular kitchen reviews karnataka" />
        <link rel="canonical" href="https://wedoointeriors.com/reviews" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Wedoo Interiors",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": data.avgRating,
            "reviewCount": data.total,
            "bestRating": "5",
            "worstRating": "1"
          }
        })}</script>
      </Helmet>

      {/* Hero */}
      <div style={{ paddingTop: '6rem', paddingBottom: '4rem', background: 'var(--ink-2)', borderBottom: '1px solid rgba(245,168,0,0.12)' }}>
        <div className="container">
          <span className="tag-label" style={{ marginBottom: '1rem', display: 'flex' }}>Client Stories</span>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2.2rem,5vw,3.5rem)', fontWeight:600, color: 'var(--cream)', marginBottom: '1rem' }}>
            What Our Clients<br /><em style={{ color: 'var(--gold)' }}>Say About Us</em>
          </h1>
          <p style={{ color: 'var(--muted)', maxWidth: '520px', lineHeight: '1.8', fontSize: '0.9rem' }}>
            Real stories from homeowners across Tumakuru and Karnataka who transformed their spaces with Wedoo Interiors.
          </p>
        </div>
      </div>

      <section className="section-pad">
        <div className="container">
          {/* Rating Summary */}
          <style>{`
            @media (max-width: 680px) {
              .rev-summary { grid-template-columns: 1fr !important; gap: 1.75rem !important; }
              .rev-summary-score { border-right: none !important; padding-right: 0 !important; border-bottom: 1px solid var(--ink-5); padding-bottom: 1.5rem; }
              .rev-form-row { grid-template-columns: 1fr !important; }
            }
          `}</style>
          {data.total > 0 && (
            <div className="rev-summary" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3rem', alignItems: 'center', background: 'var(--ink-2)', border: '1px solid var(--ink-4)', borderRadius:'var(--radius)', padding: '2.5rem', marginBottom: '3.5rem' }}>
              <div className="rev-summary-score" style={{ textAlign: 'center', borderRight: '1px solid var(--ink-5)', paddingRight: '3rem' }}>
                <div style={{ fontFamily: 'var(--font-serif)', fontSize: '5rem', color: 'var(--gold)', lineHeight: 1, fontWeight: 300 }}>{data.avgRating}</div>
                <StarRating value={Math.round(data.avgRating)} readOnly size="1.5rem" />
                <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.5rem' }}>Based on {data.total} reviews</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {ratingBreakdown.map(r => (
                  <div key={r.star} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ color: 'var(--muted)', fontSize: '0.75rem', width: '24px', textAlign: 'right' }}>{r.star}★</span>
                    <div style={{ flex: 1, height: '6px', background: 'var(--ink-5)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${r.pct}%`, background: 'var(--gold)', borderRadius: '3px', transition: 'width 1s ease' }} />
                    </div>
                    <span style={{ color: 'var(--muted)', fontSize: '0.72rem', width: '32px' }}>{r.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top row: CTA + Sources */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {SOURCES.map(s => (
                <div key={s.value} style={{ padding: '0.4rem 1rem', border: '1px solid var(--ink-5)', borderRadius:'var(--radius)', fontSize: '0.72rem', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  {s.label}
                </div>
              ))}
            </div>
            <button className="btn btn-gold" onClick={() => setShowForm(!showForm)}>
              {showForm ? '✕ Cancel' : '✍ Write a Review'}
            </button>
          </div>

          {/* Review Form */}
          {showForm && (
            <div className="card" style={{ padding: '2.5rem', marginBottom: '3rem', border: '1px solid rgba(245,168,0,0.25)', borderRadius:'var(--radius)' }}>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.5rem', marginBottom: '0.5rem', color: 'var(--cream)' }}>Share Your Experience</h3>
              <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginBottom: '2rem' }}>Your review will appear after verification (usually within 24 hours).</p>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="rev-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Your Name *</label>
                    <input {...register('name', { required: true })} placeholder="e.g. Ravi Kumar" />
                    {errors.name && <p className="form-error">Name is required</p>}
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input {...register('location')} placeholder="e.g. Tumakuru" />
                  </div>
                </div>
                <div className="rev-form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Project Type</label>
                    <select {...register('projectType')}>
                      <option value="">Select project</option>
                      {['Modular Kitchen','Interior Design','False Ceiling','Home Lighting','Aluminium & Glass','Complete Home'].map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Review Source</label>
                    <select {...register('source')}>
                      {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Your Rating *</label>
                  <StarRating value={rating} onChange={setRating} size="2rem" />
                </div>
                <div className="form-group">
                  <label>Your Review *</label>
                  <textarea {...register('review', { required: true, minLength: 30 })} rows={5}
                    placeholder="Tell us about your experience with Wedoo Interiors — the design, execution, team, and final result..." />
                  {errors.review && <p className="form-error">Please write at least 30 characters</p>}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button type="submit" className="btn btn-gold" disabled={submitting}>
                    {submitting ? 'Submitting...' : '✦ Submit Review'}
                  </button>
                  <button type="button" className="btn btn-dark" onClick={() => setShowForm(false)}>Cancel</button>
                </div>
              </form>
            </div>
          )}

          {/* Reviews Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '5rem', color: 'var(--muted)' }}>Loading reviews...</div>
          ) : data.reviews.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '5rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1.5rem' }}>⭐</div>
              <h3 style={{ fontFamily: 'var(--font-serif)', color: 'var(--cream)', marginBottom: '0.75rem' }}>Be the First to Review!</h3>
              <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>Share your Wedoo Interiors experience and help others make confident design decisions.</p>
              <button className="btn btn-gold" onClick={() => setShowForm(true)}>Write First Review</button>
            </div>
          ) : (
            <div className="grid-3" style={{ gap: '1.5rem' }}>
              {data.reviews.map(r => (
                <div key={r._id} className="testi-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', position: 'relative', zIndex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '46px', height: '46px', borderRadius: '50%', flexShrink: 0,
                        background: r.avatar ? `url(${r.avatar}) center/cover` : `linear-gradient(135deg, var(--gold-dim), var(--gold))`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: 'var(--font-serif)', fontWeight: '600', fontSize: '1.2rem', color: 'var(--ink)',
                      }}>
                        {!r.avatar && r.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '0.88rem', color: 'var(--cream)' }}>{r.name}</div>
                        {r.location && <div style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>📍 {r.location}</div>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <StarRating value={r.rating} readOnly size="0.9rem" />
                      {r.source && (
                        <div style={{ fontSize: '0.62rem', color: 'var(--muted)', marginTop: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                          via {r.source}
                        </div>
                      )}
                    </div>
                  </div>
                  {r.projectType && (
                    <span className="badge badge-gold" style={{ marginBottom: '0.85rem', display: 'inline-block', fontSize: '0.62rem' }}>
                      {r.projectType}
                    </span>
                  )}
                  <p style={{ color: 'var(--cream-2)', fontSize: '0.85rem', lineHeight: '1.8', position: 'relative', zIndex: 1 }}>
                    "{r.review}"
                  </p>
                  {r.isVerified && (
                    <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.65rem', color: '#4ade80' }}>
                      <span>✓</span> Verified Review
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
